import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    Button,
    Stack,
    InputAdornment,
    Avatar,
    IconButton,
    Tooltip
} from '@mui/material';
import { Search, Plus, SquarePen, Trash2, ArrowLeft, Eye } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { subCategoryManagementApi, deleteSubCategory } from '../../../../Services/CategoryManagement/CategoryManagementApi';
import DeleteModal from '../../../../Components/Modals/DeleteModal';
import { useGlobalSnackbar } from '../../../../Components/Common/GlobalSanckbar';
import GlobalPagination from '../../../../Components/Common/Globalpagination';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetAllSubCategories = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const { categoryId } = useParams();
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    // Pagination State
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);

    const handleViewDetails = (subCategory) => {
        navigate(`/category-management/sub-categories/${categoryId}/view-details/${subCategory.id}`, { state: { subCategory } });
    };

    const handleEdit = (subCategory) => {
        navigate(`/category-management/sub-categories/${categoryId}/edit`, { state: { subCategory } });
    };

    const handleDeleteClick = (subCategoryId) => {
        setSelectedSubCategoryId(subCategoryId);
        setDeleteModalOpen(true);
    };

    const fetchSubCategories = useCallback(async (isSearch = false, signal) => {
        try {
            if (!isSearch) setLoading(true);
            setLoading(true);

            const response = await subCategoryManagementApi(categoryId, page, rowsPerPage, searchQuery, { signal });
            if (response && response.success) {
                setSubCategories(response.data || []);
                setTotalRecords(response.totalRecords || 0);
            } else {
                if (!signal?.aborted) setError("Failed to load sub-categories.");
            }
        } catch (err) {
            if (!signal?.aborted && err.name !== 'CanceledError') {
                setError("Failed to fetch sub-categories. Please try again later.");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [categoryId, page, rowsPerPage, searchQuery]);

    const prevSearchQuery = useRef(searchQuery);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const isSearchChange = prevSearchQuery.current !== searchQuery;
        prevSearchQuery.current = searchQuery;

        if (categoryId) {
            if (isSearchChange) {
                const timer = setTimeout(() => {
                    fetchSubCategories(true, signal);
                }, 500);
                return () => {
                    clearTimeout(timer);
                    controller.abort();
                };
            } else {
                fetchSubCategories(false, signal);
                return () => {
                    controller.abort();
                };
            }
        }
        return () => controller.abort();
    }, [page, rowsPerPage, searchQuery, categoryId, fetchSubCategories]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleConfirmDelete = async () => {
        if (selectedSubCategoryId) {
            try {
                await deleteSubCategory(selectedSubCategoryId);
                setDeleteModalOpen(false);
                setSelectedSubCategoryId(null);

                const controller = new AbortController();
                fetchSubCategories(false, controller.signal);
                showSnackbar("Sub-category deleted successfully!", "success");
            } catch (err) {
                const errorMessage = err.response?.data?.message || "Failed to delete sub-category";
                showSnackbar(errorMessage, "error");
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton onClick={() => navigate('/category-management')} sx={{ color: '#666' }}>
                        <ArrowLeft size={24} />
                    </IconButton>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Sub Categories
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <TextField placeholder="Search sub-categories..." variant="outlined" size="small"
                        value={searchQuery} onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        sx={{
                            width: { xs: '100%', sm: 300 }, bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px', height: '40px',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 1 }}>
                                    <Search size={20} color="#666" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button disableElevation disableRipple disableFocusRipple disableTouchRipple variant="contained" startIcon={<Plus size={20} />}
                        onClick={() => navigate(`/category-management/sub-categories/${categoryId}/add`)}
                        sx={{ bgcolor: '#FFBD65', color: '#fff', borderRadius: '8px', px: 3 }}
                    >
                        Add Sub Category
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 1250 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                {[
                                    { label: 'S.No', align: 'left' },
                                    { label: 'Icon', align: 'left' },
                                    { label: 'Image', align: 'left' },
                                    { label: 'Sub Category Name', align: 'left' },
                                    { label: 'Slug', align: 'left' },
                                    { label: 'Display Order', align: 'left' },
                                    { label: 'Status', align: 'left' },
                                    { label: 'Created By', align: 'left' },
                                    { label: 'Updated By', align: 'left' },
                                    { label: 'Created At', align: 'left' },
                                    { label: 'Updated At', align: 'left' },
                                    { label: 'Actions', align: 'right' },
                                ].map((h) => (
                                    <TableCell
                                        key={h.label}
                                        align={h.align}
                                        sx={{
                                            fontWeight: 600,
                                            color: '#475569',
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.6px',
                                            py: 1.5,
                                            px: 1.5,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {h.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : subCategories.length > 0 ? (
                                subCategories.map((subCategory, idx) => (
                                    <TableRow key={subCategory.id} sx={{
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell align="left" sx={{ color: '#64748B', py: 1.25, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                            {(page - 1) * rowsPerPage + idx + 1}
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5 }}>
                                            <Avatar
                                                src={subCategory.icon}
                                                alt={subCategory.name}
                                                variant="rounded"
                                                sx={{ width: 34, height: 34, borderRadius: '4px', border: '1px solid #EEE' }}
                                            />
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5 }}>
                                            <Avatar
                                                src={subCategory.image}
                                                alt={subCategory.name}
                                                variant="rounded"
                                                sx={{ width: 44, height: 30, borderRadius: '4px', border: '1px solid #EEE' }}
                                            />
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 200 }}>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subCategory.name}>
                                                {subCategory.name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 180 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subCategory.slug}>
                                                {subCategory.slug || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                            {subCategory.displayOrder !== undefined && subCategory.displayOrder !== null ? subCategory.displayOrder : '-'}
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: '4px',
                                                    bgcolor: subCategory.status ? '#E8F5E8' : '#FFEBEE',
                                                    color: subCategory.status ? '#2E7D32' : '#C62828',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {subCategory.status ? "Active" : "Inactive"}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {subCategory.cuName || '-'}
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {subCategory.uuName || '-'}
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(subCategory.createdAt)}
                                        </TableCell>
                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(subCategory.updatedAt)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleViewDetails(subCategory)} sx={{ color: '#0284C7', p: 0.5 }}>
                                                        <Eye size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleEdit(subCategory)} sx={{ color: '#334155', p: 0.5 }}>
                                                        <SquarePen size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => handleDeleteClick(subCategory.id)} sx={{ color: '#C62828', p: 0.5 }}>
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                        No sub-categories found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <GlobalPagination
                    page={page}
                    pageSize={rowsPerPage}
                    totalRecords={totalRecords}
                    onPageChange={(newPage) => {
                        setPage(newPage);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onPageSizeChange={(newSize) => {
                        setRowsPerPage(newSize);
                        setPage(1);
                    }}
                    loading={loading}
                />
            </Paper>

            <DeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDelete={handleConfirmDelete}
                title="Delete Sub Category"
                description="Are you sure you want to delete this sub-category? This action cannot be undone."
            />
        </Box>
    );
};

export default GetAllSubCategories;
